import React, { useCallback, useState } from "react";
import styled, { css } from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { Modal, Input, ListGroup } from "components/bootstrap";
import useQuickJumpSearch from "components/quick-jump/hooks/useQuickJumpSearch";
import useQuickJumpKeyboardNavigation from "components/quick-jump/hooks/useQuickJumpKeyboardNavigation";
import Spinner from "components/common/Spinner";
import useDebouncedValue from "hooks/useDebouncedValue";
import SearchResultEntry from "./SearchResultEntry";
const SearchInput = styled(Input)`
  width: 100%;
`;
const List = styled.div`
  overflow: auto;
  outline: none;
`;
const SearchResults = ({ searchResults, highlightedIndex, getItemProps }) => /* @__PURE__ */ React.createElement(List, null, /* @__PURE__ */ React.createElement(ListGroup, { className: "no-bm" }, searchResults.map((item, index) => /* @__PURE__ */ React.createElement(
  SearchResultEntry,
  {
    key: item.key || item.title,
    item,
    isActive: highlightedIndex === index,
    itemProps: getItemProps(index),
    lastOpened: item.last_opened,
    favorite: item.favorite
  }
))));
const Explanation = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;
const Description = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.text.secondary};
    margin: 0;
  `
);
const QuickJumpIntro = () => {
  const { t } = useTranslation("common");
  return /* @__PURE__ */ React.createElement(Explanation, null, /* @__PURE__ */ React.createElement(Description, null, t("quick_jump.intro")), /* @__PURE__ */ React.createElement(Description, null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "quick_jump.intro_hint", components: { bold: /* @__PURE__ */ React.createElement("strong", null) } })), /* @__PURE__ */ React.createElement("br", null));
};
const NoResultsFound = () => {
  const { t } = useTranslation("common");
  return /* @__PURE__ */ React.createElement(Explanation, null, /* @__PURE__ */ React.createElement(Description, null, t("quick_jump.no_results")));
};
const SearchInputContainer = styled.div`
  display: inline-block;
  position: relative;
`;
const InputFeedback = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  min-height: 34px;
  padding-right: 3px;
`;
const QuickJumpModal = ({ onToggle }) => {
  const { t } = useTranslation("common");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);
  const { searchResults, isLoading } = useQuickJumpSearch(debouncedSearchQuery);
  const { highlightedIndex, modalProps, searchInputProps, getItemProps, onHide } = useQuickJumpKeyboardNavigation({
    items: searchResults,
    onToggle,
    searchQuery
  });
  const hasEmptySearchQuery = debouncedSearchQuery.trim() === "";
  const handleSearch = useCallback(
    (event) => {
      setSearchQuery(event.target.value);
    },
    [setSearchQuery]
  );
  return /* @__PURE__ */ React.createElement(Modal, { onHide, show: true, bsSize: "large", scrollInContent: true, rootProps: modalProps }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, t("quick_jump.title"))), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(SearchInputContainer, null, /* @__PURE__ */ React.createElement(
    SearchInput,
    {
      value: searchQuery,
      "data-autofocus": true,
      id: "quick-jump-search",
      type: "text",
      onChange: handleSearch,
      placeholder: t("quick_jump.search_placeholder"),
      ...searchInputProps
    }
  ), /* @__PURE__ */ React.createElement(InputFeedback, null, isLoading && /* @__PURE__ */ React.createElement(Spinner, { delay: 0, text: "" }))), !isLoading && searchResults.length === 0 ? /* @__PURE__ */ React.createElement(NoResultsFound, null) : /* @__PURE__ */ React.createElement(React.Fragment, null, hasEmptySearchQuery && /* @__PURE__ */ React.createElement(QuickJumpIntro, null), /* @__PURE__ */ React.createElement(
    SearchResults,
    {
      searchResults,
      getItemProps,
      highlightedIndex
    }
  ))));
};
export default QuickJumpModal;
